import { callApi, ThrowError } from '@/api/callApi';
import {
  API_ENDPOINT,
  API_URL_ORDER_SERVICE,
  API_URL_PRODUCT_SERVICE,
} from '@/api/constant';
import Breadcrumb from '@/components/common/Breadcrumb';
import CommonTable, {
  Action,
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import ImagePreview from '@/components/common/media/ImagePreview';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import Model from '@/components/common/models/Model';
import StatusDropdown from '@/components/common/StatusComponent';
import Layout from '@/components/Layout/Layout';
import AssignDriver from '@/components/order/AssignDriver';
import OrderDetailsSkeleton from '@/components/order/OrderSkeleton';
import OrderStatusUpdate from '@/components/order/OrderStatus';
import TerminalView from '@/components/order/Terminal';
import Card from '@/components/UI/Card';
import DetailsCard from '@/components/UI/DetailsCard';
import useDownloadInvoice from '@/hooks/useDownloadInvoice';
import { IErpLogs } from '@/interfaces/IErpLogs.interface';
import { Order, OrderItem } from '@/interfaces/order.interface';
import {
  formatDateTime,
  formatDeliveryRange,
  getPaymentMethodMessage,
  OrderStatus,
  parseToJson,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { TerminalSquare } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface StatesProp {
  isLoading: boolean;
  orderInfo: Order | null;
  erpLogs: IErpLogs | null;
  showLogs: boolean;
  showPaymentModel: boolean;
}
const OrderDetail: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [states, setStates] = useState<StatesProp>({
    isLoading: false,
    orderInfo: null,
    erpLogs: null,
    showLogs: false,
    showPaymentModel: false,
  });
  const navigate = useNavigate();
  const { downloadInvoice } = useDownloadInvoice();
  const { id } = useParams();
  const orderId = id;
  const breadcrumb = [
    { label: 'Orders', href: RoutesLink.order },
    { label: 'Details' },
  ];

  const excludeHover = [
    OrderStatus.CANCELED,
    OrderStatus.DELIVERED,
    OrderStatus.REFUNDED,
    OrderStatus.REFUND_IN_PROGRESS,
    OrderStatus.REFUND_REJECTED,
    OrderStatus.REFUND_REQUESTED,
  ];

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const fetchOrderDetail = async () => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}/${orderId}`;
      const res = await callApi(PATH, { method: 'GET' });
      handleChangeState({ orderInfo: res, isLoading: false });
    } catch (error) {
      ThrowError(error);
      handleChangeState({ isLoading: false });
    }
  };

  const updatePaymentStatus = async () => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}/update-status`;
      await callApi(PATH, {
        method: 'PATCH',
        data: { id, paymentStatus: 'PAID' },
      });
      showToastNotification('success', 'payment status updated to paid.');
      fetchOrderDetail();
    } catch (error) {
      ThrowError(error);
      handleChangeState({ isLoading: false });
    }
  };

  const viewErpLogs = async () => {
    try {
      const PATH = `${API_URL_ORDER_SERVICE}/${API_ENDPOINT.erp}/log/${orderId}`;
      const res = await callApi<IErpLogs>(PATH, { method: 'GET' });
      handleChangeState({ erpLogs: res });
    } catch (error) {
      ThrowError(error);
    }
  };

  useEffect(() => {
    if (orderId) {
      fetchOrderDetail();
      viewErpLogs();
    }
  }, [orderId]);

  const customerDetail = [
    { label: 'First Name', value: states?.orderInfo?.personalInfo?.firstName },
    { label: 'Last Name', value: states?.orderInfo?.personalInfo?.lastName },
    { label: 'Email', value: states?.orderInfo?.personalInfo?.email },
    { label: 'Phone Number', value: states?.orderInfo?.personalInfo?.phone },
    { label: 'Device Type', value: states?.orderInfo?.deviceType },
    ...(states.orderInfo?.paymentMethod?.details?.brand &&
    states.orderInfo?.paymentMethod?.details?.number
      ? [
          {
            label: 'Card Name',
            value: `${states?.orderInfo?.paymentMethod?.details?.brand || '--'}`,
          },
        ]
      : []),
    ...(states.orderInfo?.paymentMethod?.details?.provider
      ? [
          {
            label: 'Provider',
            value: `${states?.orderInfo?.paymentMethod?.details?.provider || '--'}`,
          },
        ]
      : []),
  ];

  const orderSummary = [
    { label: 'Order ID', value: states?.orderInfo?.orderId },
    {
      label: 'Total',
      value: `BHD ${toFixedThree(states?.orderInfo?.total || 0)}`,
    },
    { label: 'Order Status', value: states?.orderInfo?.status },
    {
      label: 'Payment by Wallet',
      value: `BHD ${toFixedThree(states?.orderInfo?.paidByWallet || 0)}`,
    },
    {
      label: 'Payment Status',
      value: states?.orderInfo?.paymentStatus,
      render: () => {
        return (
          states.orderInfo && (
            <StatusDropdown
              value={states?.orderInfo?.paymentStatus}
              options={
                states?.orderInfo?.paymentStatus !== 'PAID'
                  ? [
                      {
                        value: 'PAID',
                        label: 'Paid',
                        textClass: 'text-blue-500',
                        bgClass: 'bg-blue-500',
                      },
                    ]
                  : []
              }
              onChange={() => handleChangeState({ showPaymentModel: true })}
              canEditVal="order"
            />
          )
        );
      },
    },
    {
      label: 'Grand Total',
      value: `BHD ${toFixedThree(states?.orderInfo?.grandTotal || 0)}`,
    },
    {
      label: 'Order Date Time',
      value: formatDateTime(states?.orderInfo?.createdAt, 'YYMMMDDHH'),
    },
    {
      label: 'Discount',
      value: `BHD ${toFixedThree(states?.orderInfo?.discountedValue || 0)}`,
    },
    {
      label: 'Payment Type',
      value: getPaymentMethodMessage(
        states.orderInfo?.paymentMethod.type || '',
        states.orderInfo?.paymentStatus,
      ),
    },
    ...(states.orderInfo?.personalInfo?.isEmployee
      ? [
          {
            label: 'Employee Discount',
            value: `BHD ${toFixedThree(states?.orderInfo?.employeeDiscountAmount || 0)}`,
          },
        ]
      : []),
    {
      label: 'Transaction ID',
      value: states?.orderInfo?.chargeId || '',
    },
    {
      label: 'Loyalty Points',
      value: `${toFixedThree(states?.orderInfo?.points || 0)}`,
    },
  ];

  const deliveredInfo = states?.orderInfo?.previousStatus?.find(
    (v) => v.status === OrderStatus.DELIVERED,
  );

  const deliveryInfo = [
    {
      label: 'Address Type',
      value: states?.orderInfo?.address?.addressType || '',
    },
    {
      label: 'Delivery Charges',
      value: `BD ${states?.orderInfo?.deliveryFees || '0.000'}`,
    },
    {
      label: deliveredInfo ? 'Delivered At' : 'Expected Develivery At',
      value: deliveredInfo?.timestamp
        ? formatDateTime(deliveredInfo?.timestamp, 'YYMMMDDHH')
        : formatDeliveryRange(states?.orderInfo?.formattedDeliveryTime),
    },
    {
      label: 'Building Name',
      value: states?.orderInfo?.address?.buildingName,
    },
    {
      label: 'Apartment Number',
      value: states?.orderInfo?.address?.aptNumber,
    },
    {
      label: 'Floor',
      value: states?.orderInfo?.address?.floor,
    },
    {
      label: 'Street',
      value: states?.orderInfo?.address?.street,
    },
    {
      label: 'Block',
      value: states?.orderInfo?.address?.block,
    },
    {
      label: 'Delivery Address',
      value: states?.orderInfo?.address?.addressLabel,
    },
    {
      label: 'Company Name',
      value: states?.orderInfo?.address?.companyName,
    },
    {
      label: 'Delivery Instruction',
      value: states?.orderInfo?.deliveryInstruction,
    },
    {
      label: 'Special Instruction',
      value: states?.orderInfo?.specialInstruction,
    },
  ];

  // const driverInfo = [
  //   {
  //     label: 'Driver Name',
  //     value: `${states?.orderInfo?.driverDetail?.fullName}`,
  //   },
  //   {
  //     label: 'Driver Email',
  //     value: `${states?.orderInfo?.driverDetail?.email}`,
  //   },
  //   {
  //     label: 'Driver Phone',
  //     value: `${states?.orderInfo?.driverDetail?.phone}`,
  //   },
  // ];

  // const RefundCardDetails = [
  //   {
  //     label: 'Refund Info',
  //     value: `An amount of ${parseFloat(states.orderInfo?.refundInfo?.amount || '0').toFixed(3)} has been transferred to the user's Meatery wallet.`,
  //   },
  // ];
  // const otherSummary = [
  //   {label: 'Points Earned', value: states.orderInfo.}
  // ];

  const actions: Action<OrderItem>[] = [
    {
      text: 'View',
      onClick: (product) => {
        const id =
          product.type === 'bundle'
            ? product?.bundleDetail?.id
            : product?.itemDetail?.product?.id;
        const PATH =
          product.type === 'bundle'
            ? RoutesLink.collectionDetails
            : RoutesLink.productDetail;
        navigate(`${PATH}/${id}`);
      },
    },
  ];

  const productsColumn: TableColumn<OrderItem>[] = [
    {
      key: 'image',
      header: 'Image',
      render: (item) => {
        const url = item?.itemDetail?.product?.directImageUrl
          ? item?.itemDetail?.product?.directImageUrl
          : item?.itemDetail?.product?.images?.[0]?.url;
        const src =
          item.type === 'bundle' ? item?.bundleDetail?.media[0]?.url : url;
        const name =
          item.type === 'bundle'
            ? item?.bundleDetail?.title
            : item?.itemDetail?.product?.name;
        return (
          <ImagePreview
            src={`${API_URL_PRODUCT_SERVICE}/${src}`}
            alt=""
            text={name}
          />
        );
      },
    },
    {
      key: 'orderedQuantity',
      header: 'Quantity',
    },
    {
      key: 'Type',
      header: 'Order Type',
      render: (item) => {
        const type = item.type === 'bundle' ? item?.type : item?.uom;
        return type;
      },
    },
    {
      key: 'price',
      header: 'Amount BHD',
      render: (item) => <span>{toFixedThree(item.price)}</span>,
    },
  ];

  return (
    <Layout>
      {states.isLoading ? (
        <OrderDetailsSkeleton />
      ) : (
        <>
          <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
            <Breadcrumb items={breadcrumb} />
            <div className="flex items-center gap-3">
              {/* {states.orderInfo?.status === OrderStatus.ACCEPTED && (
            <button
              onClick={() => setIsOpen(true)}
              className="text-base p-3 bg-white text-black rounded-2xl"
            >
              <span>
                {states?.orderInfo?.driverDetail
                  ? 'Update Driver'
                  : 'Assign Driver'}{' '}
                +
              </span>
            </button>
          )} */}
              {states.orderInfo && (
                <div
                  className={`text-base text-black p-3 bg-blue-300 ${excludeHover.includes(states?.orderInfo?.status as OrderStatus) ? 'cursor-auto!' : 'hover:bg-primary-light'} rounded-2xl`}
                >
                  <OrderStatusUpdate
                    onSucess={() => fetchOrderDetail()}
                    order={states.orderInfo}
                  />
                </div>
              )}
              {![OrderStatus.CANCELED, OrderStatus.FAILED].includes(
                states?.orderInfo?.status as OrderStatus,
              ) && (
                <button
                  className="text-base text-black p-3 bg-blue-300 hover:bg-primary-light rounded-2xl"
                  onClick={() => orderId && downloadInvoice(orderId)}
                >
                  View Order Confirmation
                </button>
              )}
              <button
                title="View Logs"
                className="text-base text-black p-3 bg-blue-300 hover:bg-primary-light rounded-2xl"
                onClick={() => handleChangeState({ showLogs: true })}
              >
                <TerminalSquare />
              </button>
            </div>
          </div>
          <div>
            {/* Main Grid */}
            <div
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              uk-scrollspy="target: > div; cls: uk-animation-slide-bottom-small; delay: 100"
            >
              <DetailsCard
                title="Customer Information"
                details={customerDetail}
              />
              {/* DRIVER INFO  */}
              {/* {states?.orderInfo?.driverDetail && (
            <DetailsCard title="Driver Information" details={driverInfo} />
          )} */}
              {/* Order Summary */}
              <DetailsCard title="Order Summary" details={orderSummary} />

              {/* Delivery Information */}
              <div className="col-span-2">
                <DetailsCard
                  title="Delivery Information"
                  details={deliveryInfo}
                />
              </div>

              {/* <DetailsCard details={RefundCardDetails} /> */}

              {/* Other Summary */}
            </div>
          </div>
          {states.orderInfo?.orderItems.length && (
            <div className="mt-4">
              <Card>
                <CommonTable
                  columns={productsColumn}
                  data={states.orderInfo?.orderItems}
                  minHeight="44"
                  actions={actions}
                />
              </Card>
            </div>
          )}
          {isOpen && states.orderInfo && (
            <AssignDriver
              id={states.orderInfo.id}
              setIsOpen={setIsOpen}
              onSuccess={() => {
                (fetchOrderDetail(), setIsOpen(false));
              }}
              driverId={states.orderInfo.driverId || ''}
            />
          )}
          <Model
            show={states.showLogs}
            onClose={() => handleChangeState({ showLogs: false })}
            size="w-4xl"
            height="max-h-[500px]"
          >
            <TerminalView
              content={{
                payload: states.erpLogs
                  ? parseToJson(states.erpLogs.payload)
                  : {},
                response: states.erpLogs
                  ? parseToJson(states.erpLogs.response)
                  : {},
              }}
            />
          </Model>
          {states.showPaymentModel && (
            <>
              <ConfirmationModel
                modelType="update"
                show={states.showPaymentModel}
                onClose={() => {
                  handleChangeState({ showPaymentModel: false });
                }}
                onConfirm={() => updatePaymentStatus()}
                type={`Paid`}
                subTitle="Are you sure you want to update payment status to"
              />
            </>
          )}
        </>
      )}
    </Layout>
  );
};

export default OrderDetail;
